import React, { useState, useEffect, useMemo } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import "./RhymingQuiz.scss";
import { sleep, shuffle, getRandomElements } from "../shared/util";
import useConversation from "../shared/useConversation";
import useVersion from "../shared/useVersion";
import useTimer from "../shared/useTimer";
import { Container, Word } from "../shared";

import icon from "./rhymingquiz-icon.png";
import {getUserData} from "Services/API";
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";

let ttsStrings;

export default function RhymingQuizValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.rhyming_quiz;

  const { id } = useVersion(props.wordMetadata, {
    1: ["RHYME", "NSYL"],
  });

  const versionId = props.version || id;

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!versionId) close();
  }, [versionId]);

  if (!versionId) return null;

  const Version = {
    1: RhymingQuiz,
  }[versionId];

  return <Version {...props} close={close} isMountedRef={isMountedRef} />;

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function RhymingQuiz(props) {
  const { word, wordMetadata, speech } = props;
  const { speak, speakFile, speakSequence } = useConversation(speech);
  const { cancellablePromise } = useCancellablePromise();

  wordMetadata.RHYME = wordMetadata.RHYME.trim(); // Fixes case where RHYME metadata has a trailing space
  const { RHYME, NSYL } = wordMetadata;

  const [stage, setStage] = useState(0);
  const [canClick, setCanClick] = useState(false);

  const maxTime = useTimer({
    timeout: 10000,
    onEnd: async () => {
      if (!props.isMountedRef.current) return;
      setStage(2);
      if(getUserData().bilingualMode) {
        await speakSequence([
          {textToSpeak: RHYME, skipTranslation: true},
          ttsStrings.rhymes_with,
          {textToSpeak: word, skipTranslation: true},
          ttsStrings.back_to_reading,
        ]);
      } else {
        await speak(`${RHYME} ${ttsStrings.rhymes_with} ${word}. ${ttsStrings.back_to_reading}`);
      }
      props.close();
    },
  });

  const { options, target } = useMemo(() => {
    const distractors = getDistractors(NSYL, RHYME);
    const options = shuffle([...distractors, RHYME]);
    return { options, target: RHYME };
  }, []);

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0:
          await cancellablePromise(speak(ttsStrings.intro));
          await cancellablePromise(sleep(2000));
          setStage(1);
          break;
        case 1:
          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.our_word_is,
              {textToSpeak: word, skipTranslation: true},
              ttsStrings.which_rhymes_with,
              {textToSpeak: word, skipTranslation: true},
              ttsStrings.click_to_choose(),
            ]));
          } else {
            await cancellablePromise(speak(`${ttsStrings.our_word_is} "${word}". ${ttsStrings.which_rhymes_with} "${word}". ${ttsStrings.click_to_choose()}`));
          }
          setCanClick(true);
          maxTime.startTimer();
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return <Container icon={icon}>{renderContent()}</Container>;

  function renderContent() {
    if (stage === 0) return <Word>{word}</Word>;

    if (stage === 1) {
      const optionStyle = {};
      if (NSYL > 2) {
        optionStyle.fontSize = 40;
        optionStyle.margin = "0 20px";
      }
      if (!canClick) {
        optionStyle.color = "#999";
        optionStyle.cursor = "default";
      }
      return (
        <div className="rq-options" role="radiogroup" aria-required="true" aria-label="options">
          {options.map((option, i) => {
            const onOptionClickHandler = async () => {
              try {
                if (!canClick) return;
                maxTime.clearTimer();
                setStage(2);
                if (option === target) {
                  // correct
                  if(getUserData().bilingualMode) {
                    await cancellablePromise(speakSequence([
                      ttsStrings.you_are_right,
                      {textToSpeak: RHYME, skipTranslation: true},
                      ttsStrings.rhymes_with,
                      {textToSpeak: word, skipTranslation: true},
                      ttsStrings.back_to_reading,
                    ]));
                  } else {
                    await cancellablePromise(speak(`${ttsStrings.you_are_right} ${RHYME} ${ttsStrings.rhymes_with} ${word}. ${ttsStrings.back_to_reading}`));
                  }
                  props.close();
                } else {
                  // incorrect
                  if(getUserData().bilingualMode) {
                    await cancellablePromise(speakSequence([
                      ttsStrings.no,
                      {textToSpeak: option, skipTranslation: true},
                      ttsStrings.doesnt_rhyme_with,
                      {textToSpeak: word, skipTranslation: true},
                      ttsStrings.but,
                      {textToSpeak: RHYME, skipTranslation: true},
                      ttsStrings.does,
                      ttsStrings.back_to_reading,
                    ]));
                  } else {
                    await cancellablePromise(speak(`${ttsStrings.no}, "${option}" ${ttsStrings.doesnt_rhyme_with} "${word}". ${ttsStrings.but} "${RHYME}" ${ttsStrings.does}. ${ttsStrings.back_to_reading}`));
                  }
                  props.close();
                }
              } catch (error) {
                if (error.isCancelled) return;
                throw(error);
              }
            }

            const accessibleOptionAttributes = {
              ...doNotUseAccessibleStaticElementInteractions({onClick: onOptionClickHandler}),
              role: "radio",
              "aria-label": option,
              "aria-checked": false,
            }

            return (
              <div
                key={i}
                className="rq-option"
                style={optionStyle}
                {...accessibleOptionAttributes}
              >
                {option}
              </div>
            )
          })}
        </div>
      );
    }

    if (stage === 2) {
      return (
        <div className="rhyme-wrapper">
          <Word>{RHYME}</Word>
          <div className="rhymes-with">
            <div>rhymes</div>
            <div>with</div>
          </div>
          <Word>{word}</Word>
        </div>
      );
    }
  }
}

// generate distractors based on syllable count of trigger word
function getDistractors(nSyl, target) {
  const words = getUserData().locale === 'es-mx' ? spanishDistractorPool[nSyl] : englishDistractorPool[nSyl];

  // distractors must be different than the target word
  const pool = words.filter((w) => w.toLowerCase() !== target.toLowerCase());
  return getRandomElements(pool, 2);
}

// distractor words by syllable count
const englishDistractorPool = {
  1: [
    "dog",
    "owl",
    "fish",
    "cat",
    "shed",
    "drum",
    "brick",
    "plug",
    "horn",
    "trap",
    "cake",
    "boy",
    "game",
    "love",
    "day",
    "man",
    "ate",
    "life",
    "dad",
  ],
  2: [
    "pencil",
    "dolphin",
    "spider",
    "lighthouse",
    "movie",
    "window",
    "baby",
    "cuddle",
    "rabbit",
  ],
  3: [
    "piano",
    "elephant",
    "library",
    "telephone",
    "strawberry",
    "computer",
    "butterfly",
    "aeroplane",
    "caravan",
  ],
  4: [
    "supermarket",
    "impossible",
    "watermelon",
    "calculator",
    "helicopter",
    "television",
    "information",
    "competition",
    "crocodile",
  ],
};

const spanishDistractorPool = {
  1: [
    "sol",
    "pan",
    "ver",
    "dar",
    "ir",
    "flor",
    "paz",
    "gol",
    "mar",
    "luz",
    "tren",
    "gas",
    "cruz",
    "yo",
    "sí",
    "no",
    "dos",
    "que",
    "pez",
  ],
  2: [
    "tapón",
    "casa",
    "perro",
    "silla",
    "balón",
    "taco",
    "bebé",
    "dedo",
    "fresa",
    "baño",
    "gato",
    "boca",
    "ratón",
    "vaca",
    "zorro",
    "hoja",
    "sapo",
  ],
  3: [
    "payaso",
    "tomate",
    "basura",
    "pájaro",
    "diseñar",
    "empacar",
    "practicar",
    "camino",
    "amigo",
  ],
  4: [
    "bicicleta",
    "película",
    "generoso",
    "lapicero",
    "compañero",
    "pasajero",
    "fantástico",
    "imaginar",
    "escalera",
  ],
};
